import { useEffect, useState } from 'react';

interface UseCountdownTimerProps {
  totalTimeMs: number;
  onTimeExpired?: () => void;
  shouldReset?: boolean;
}

interface CountdownResult {
  timeLeft: {
    minutes: number;
    seconds: number;
  };
  isExpired: boolean;
  progress: number;
}

export const useCountdownTimer = ({
  totalTimeMs,
  onTimeExpired,
  shouldReset = false,
}: UseCountdownTimerProps): CountdownResult => {
  const [timeLeftMs, setTimeLeftMs] = useState(totalTimeMs);
  const [isExpired, setIsExpired] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    return () => {
      setTimeLeftMs(0);
      setIsExpired(false);
      setIsInitialized(false);
    };
  }, []);

  useEffect(() => {
    if (totalTimeMs > 0) {
      setTimeLeftMs(totalTimeMs);
      setIsExpired(false);
      setIsInitialized(true);
    }
  }, [totalTimeMs]);

  useEffect(() => {
    if (shouldReset) {
      setTimeLeftMs(0);
      setIsExpired(true);
      setIsInitialized(false);
    }
  }, [shouldReset]);

  useEffect(() => {
    if (!isInitialized || timeLeftMs <= 0 || shouldReset) {
      if (isInitialized && timeLeftMs <= 0 && !shouldReset) {
        setIsExpired(true);
        onTimeExpired?.();
      }
      return;
    }

    const interval = setInterval(() => {
      setTimeLeftMs((prev) => {
        const newTime = prev - 1000;
        if (newTime <= 0) {
          setIsExpired(true);
          onTimeExpired?.();
          return 0;
        }
        return newTime;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [timeLeftMs, onTimeExpired, isInitialized, shouldReset]);

  const minutes = Math.floor(timeLeftMs / 60000);
  const seconds = Math.floor((timeLeftMs % 60000) / 1000);
  const progress = totalTimeMs > 0 ? (timeLeftMs / totalTimeMs) * 100 : 0;

  return {
    timeLeft: { minutes, seconds },
    isExpired,
    progress,
  };
};
